<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Transactions KL Pay</h1>
        <p class="page-subtitle">{{ filteredResults.length }} transaction{{ filteredResults.length > 1 ? 's' : '' }}</p>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="search"
                placeholder="Rechercher..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                :items="statuses"
                item-value="id"
                item-text="label"
                placeholder="Statut"
                v-model="filterStatus"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                :items="walletFilters"
                item-value="id"
                item-text="label"
                placeholder="Wallet"
                v-model="filterWallet"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn outlined small @click="reset" class="mr-1">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn color="success" outlined small @click="exportExcel">
                  <v-icon small>mdi-file-excel</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th>MONTANT VENTE</th>
                <th>FRAIS</th>
                <th>DÉBITÉ</th>
                <th>WALLET</th>
                <th>PAYEUR</th>
                <th>RÉFÉRENCE</th>
                <th>STATUT</th>
                <th>DATE</th>
                <th>HEURE</th>
              </tr>
              <tr class="totals-row">
                <th class="text-left">TOTAL ({{ filteredResults.length }} transactions)</th>
                <th>{{ formatCurrency(totalAmount) }} HTG</th>
                <th colspan="8"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredResults" :key="item.id">
                <td class="text-left font-weight-bold">
                  <!-- Numéro cliquable seulement si la vente existe (transaction réussie) :
                       une transaction échouée peut n'avoir jamais créé de fiche. -->
                  <a v-if="item.status === 'CONFIRMED'" class="order-link" @click.prevent="openSale(item.order_number)">#{{ item.order_number }}</a>
                  <span v-else>#{{ item.order_number }}</span>
                </td>
                <td>{{ formatCurrency(item.sale_amount) }} {{ item.currency }}</td>
                <td>{{ formatCurrency(item.fee_amount) }}</td>
                <td>{{ formatCurrency(item.amount) }}</td>
                <td>
                  <v-chip x-small outlined :color="walletOf(item) === 'MonCash' ? 'deep-orange' : 'blue'">
                    {{ walletOf(item) }}
                  </v-chip>
                </td>
                <td>{{ item.payer || '--' }}</td>
                <td>{{ item.transaction_id || '--' }}</td>
                <td>
                  <v-chip x-small :color="getStatusChipColor(item.status)" :text-color="getStatusChipTextColor(item.status)">
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </td>
                <td class="text-caption">{{ item.created_at | formatDate }}</td>
                <td class="text-caption">{{ item.created_at | formatTime }}</td>
              </tr>
              <tr v-if="filteredResults.length === 0">
                <td colspan="10" class="text-center py-4 grey--text">Aucune transaction</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import PreloaderComponent from '../../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../../components/BreadcrumbComponent.vue'

// Mêmes préfixes Digicel que le routage serveur (KlPayService::DIGICEL_PREFIXES)
const DIGICEL_PREFIXES = ['30', '31', '34', '36', '37', '38', '39', '44', '46', '47', '48', '49']

export default {
  name: 'KlPayTransactionsView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    results: [],
    enable: false,
    filterStatus: null,
    filterWallet: null,
    statuses: [
      { id: 'CONFIRMED', label: 'Confirme' },
      { id: 'PENDING', label: 'En attente' },
      { id: 'DENIED', label: 'Refuse' },
      { id: 'CANCELLED', label: 'Annule' },
    ],
    walletFilters: [
      { id: 'MonCash', label: 'MonCash' },
      { id: 'NatCash', label: 'NatCash' },
    ],
    items: [
      { text: 'ACCUEIL', disabled: false, href: '/' },
      { text: 'KL PAY', disabled: false, href: '/klpay/settings' },
      { text: 'TRANSACTIONS', disabled: true, href: '/payments/klpay' },
    ],
  }),
  computed: {
    filteredResults() {
      let data = this.results
      if (this.filterStatus) {
        data = data.filter(item => item.status === this.filterStatus)
      }
      if (this.filterWallet) {
        data = data.filter(item => this.walletOf(item) === this.filterWallet)
      }
      if (this.search) {
        const s = this.search.toLowerCase()
        data = data.filter(item =>
          (item.order_number && String(item.order_number).toLowerCase().includes(s)) ||
          (item.payer && item.payer.toLowerCase().includes(s)) ||
          (item.transaction_id && item.transaction_id.toLowerCase().includes(s)) ||
          (item.sale_amount && String(item.sale_amount).includes(s))
        )
      }
      return data
    },
    totalAmount() {
      return this.filteredResults.reduce((sum, item) => sum + parseFloat(item.sale_amount || 0), 0)
    },
  },
  methods: {
    // Retrace la vente : recherche pré-remplie sur le numéro de fiche (order_number).
    openSale(orderNumber) {
      if (!orderNumber) return
      this.$router.push({ name: 'search', query: { q: orderNumber } })
    },
    // Wallet réellement visé, déduit du préfixe du payeur (même règle que le serveur)
    walletOf(item) {
      const digits = String(item.payer || '').replace(/\D/g, '')
      const last8 = digits.length > 8 ? digits.slice(-8) : digits
      return DIGICEL_PREFIXES.includes(last8.substring(0, 2)) ? 'MonCash' : 'NatCash'
    },
    formatCurrency(value) {
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    load() {
      this.enable = false
      const self = this
      const business_id = JSON.parse(localStorage.business).id

      let url = '/payment-requests?processor_name=KLPAY&business_id=' + business_id

      const debut = localStorage.getItem('debut_periode')
      const fin = localStorage.getItem('fin_periode')
      if (debut && debut !== 'null' && fin && fin !== 'null') {
        url += '&from=' + debut + '&to=' + fin
      }

      this.$axios.get(url)
        .then(function (response) {
          self.results = response.data
          self.enable = true
        }).catch(function () {
          self.enable = true
        })
    },
    reset() {
      this.search = ''
      this.filterStatus = null
      this.filterWallet = null
    },
    getStatusChipColor(status) {
      if (status === 'CONFIRMED') return 'green lighten-4'
      if (status === 'PENDING') return 'blue lighten-4'
      return 'red lighten-4'
    },
    getStatusChipTextColor(status) {
      if (status === 'CONFIRMED') return 'green darken-3'
      if (status === 'PENDING') return 'blue darken-3'
      return 'red darken-3'
    },
    getStatusText(status) {
      if (status === 'CONFIRMED') return 'Confirme'
      if (status === 'PENDING') return 'En attente'
      if (status === 'CANCELLED') return 'Annule'
      return 'Refuse'
    },
    async exportPDF() {
      const { jsPDF } = await import('jspdf')
      const biz = JSON.parse(localStorage.business || '{}')
      const doc = new jsPDF('l', 'mm', 'a4')
      const pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml

      doc.setFillColor(25, 65, 115); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('TRANSACTIONS KL PAY', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      if (biz.name) doc.text(biz.name, mr, 11, { align: 'right' })

      let y = 24
      const cols = [
        { label: '#', w: 28, align: 'left' },
        { label: 'MONTANT', w: 28, align: 'right' },
        { label: 'FRAIS', w: 20, align: 'right' },
        { label: 'DEBITE', w: 26, align: 'right' },
        { label: 'WALLET', w: 22, align: 'center' },
        { label: 'PAYEUR', w: 34, align: 'left' },
        { label: 'REFERENCE', w: 44, align: 'left' },
        { label: 'STATUT', w: 24, align: 'center' },
        { label: 'DATE', w: 24, align: 'center' },
        { label: 'HEURE', w: 18, align: 'center' },
      ]
      const totalW = cols.reduce((s, c) => s + c.w, 0)
      const scale = cw / totalW
      cols.forEach(c => { c.w = Math.round(c.w * scale) })
      cols[6].w += (cw - cols.reduce((s, c) => s + c.w, 0))
      let cx = ml; cols.forEach(c => { c.x = cx; cx += c.w })
      const rowH = 6.5
      const self = this

      function drawHeader() {
        doc.setFillColor(25, 65, 115); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(c => {
          const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          doc.text(c.label, tx, y + 4.5, { align: c.align })
        })
        y += rowH + 1
      }

      drawHeader()
      this.filteredResults.forEach(function (item, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(220, 225, 230); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59)
        const vals = [
          '#' + (item.order_number || ''),
          self.formatCurrency(item.sale_amount),
          self.formatCurrency(item.fee_amount),
          self.formatCurrency(item.amount),
          self.walletOf(item),
          (item.payer || '--').substring(0, 16),
          (item.transaction_id || '--').substring(0, 26),
          self.getStatusText(item.status),
          new Date(item.created_at).toLocaleDateString('fr-FR'),
          new Date(item.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        ]
        cols.forEach((c, i) => {
          const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      y += 1
      doc.setFillColor(248, 250, 252); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(25, 65, 115); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59)
      doc.text('TOTAL (' + this.filteredResults.length + ' transactions)', ml + 3, y + 4.8)
      doc.text(this.formatCurrency(this.totalAmount) + ' HTG', cols[1].x + cols[1].w - 3, y + 4.8, { align: 'right' })

      const totalPages = doc.internal.getNumberOfPages()
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setDrawColor(220, 225, 230); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(25, 65, 115)
        doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139)
        doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        const now = new Date()
        doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('transactions_klpay.pdf')
    },
    exportExcel() {
      const XLSX = require('xlsx')
      const biz = JSON.parse(localStorage.business || '{}')
      const wsData = []
      wsData.push(['TRANSACTIONS KL PAY'])
      wsData.push([biz.name || ''])
      wsData.push([new Date().toLocaleDateString('fr-FR')])
      wsData.push([])
      wsData.push(['#', 'Montant vente', 'Frais', 'Débité', 'Wallet', 'Payeur', 'Référence', 'Statut', 'Date', 'Heure'])

      this.filteredResults.forEach(item => {
        wsData.push([
          item.order_number || '',
          Math.round(parseFloat(item.sale_amount || 0) * 100) / 100,
          Math.round(parseFloat(item.fee_amount || 0) * 100) / 100,
          Math.round(parseFloat(item.amount || 0) * 100) / 100,
          this.walletOf(item),
          item.payer || '--',
          item.transaction_id || '--',
          this.getStatusText(item.status),
          new Date(item.created_at).toLocaleDateString('fr-FR'),
          new Date(item.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        ])
      })
      wsData.push([])
      wsData.push(['TOTAL (' + this.filteredResults.length + ' transactions)', Math.round(this.totalAmount * 100) / 100])

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 8 }]
      XLSX.utils.book_append_sheet(wb, ws, 'Transactions KL Pay')
      XLSX.writeFile(wb, 'transactions_klpay.xlsx')
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped>
.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { position: sticky; top: 0; background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px; text-align: center; border-bottom: 2px solid #e8ecf1; white-space: nowrap; z-index: 1; }
.report-table .totals-row th { background: #f1f5f9; color: #1a2332; font-weight: 700; font-size: 0.75rem; border-bottom: 2px solid #cbd5e1; }
.report-table tbody td { padding: 10px 12px; text-align: center; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
.gap-2 { gap: 4px; }
.order-link { color: #194173; cursor: pointer; text-decoration: underline; }
.order-link:hover { color: #0d2847; }
</style>
